import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useGetCarriers, type Carrier } from "@/api/carriers";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { cn } from "@/lib/utils";

/** Persisted header selection; shipment screens also offer external carriers. */
export function CarrierFilter() {
  const { carrierIds, setCarrierIds, includeExternalCarriers } =
    useCarrierScope();
  const { data } = useGetCarriers();
  const carriers = data?.data ?? [];

  const selectableCarriers = useMemo(
    () =>
      sortCarriers(
        includeExternalCarriers
          ? carriers
          : carriers.filter((c) => c.selfRated || carrierIds.includes(c.id))
      ),
    [carriers, includeExternalCarriers, carrierIds]
  );
  const total = selectableCarriers.length;
  const selectedCount = carrierIds.length;

  const [open, setOpen] = useState(false);

  const allSelected =
    total > 0 && selectableCarriers.every((c) => carrierIds.includes(c.id));
  const noneSelected = selectedCount === 0;

  const summary = noneSelected
    ? "All carriers · no filter"
    : allSelected
      ? "All carriers"
      : `${selectedCount} of ${total} carriers`;

  function toggle(id: string) {
    if (carrierIds.includes(id)) {
      setCarrierIds(carrierIds.filter((x) => x !== id));
    } else {
      setCarrierIds([...carrierIds, id]);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label="Carrier filter"
          className={cn(
            "w-[220px] h-9 px-3 justify-between text-sm",
            // No amber. Amber means "something is wrong" — an unfiltered view is
            // the default, not a fault. Only an ACTIVE subset is worth colouring,
            // because that is the state where what you see is not everything.
            !noneSelected &&
              !allSelected &&
              "bg-accent border-primary/30 text-accent-foreground"
          )}
        >
          <span className="truncate">Carriers: {summary}</span>
          <ChevronDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[280px] p-2">
        <div className="flex items-center justify-between gap-2 px-2 pb-2 border-b">
          <button
            type="button"
            className="text-xs font-medium text-blue-700 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={allSelected || total === 0}
            onClick={() => setCarrierIds(selectableCarriers.map((c) => c.id))}
          >
            Select all
          </button>
          <button
            type="button"
            className="text-xs font-medium text-gray-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={noneSelected}
            onClick={() => setCarrierIds([])}
          >
            Clear all
          </button>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          {selectableCarriers.length === 0 ? (
            <div className="px-2 py-3 text-xs text-gray-500">
              No carriers found.
            </div>
          ) : (
            selectableCarriers.map((c) => {
              const checked = carrierIds.includes(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(c.id)}
                    aria-label={c.name}
                  />
                  <span className="text-sm truncate flex-1">{c.name}</span>
                  {c.code === "ez" && (
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      owned
                    </span>
                  )}
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Exported so tests can pin the ordering / self-rated-filter contract.
export function selfRatedOnly(carriers: Carrier[]): Carrier[] {
  return carriers.filter((c) => c.selfRated);
}

export function sortCarriers(carriers: Carrier[]): Carrier[] {
  return [...carriers].sort((a, b) => {
    const aPrimary = a.code === "ez";
    const bPrimary = b.code === "ez";
    if (aPrimary && !bPrimary) return -1;
    if (!aPrimary && bPrimary) return 1;
    return a.name.localeCompare(b.name);
  });
}
